import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { FormDialog } from "@/components/common/FormDialog";
import { Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SearchInput } from "@/components/common/SearchInput";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const EXPENSE_CATEGORIES = ["Infrastructure", "Software", "Events", "Travel", "Other"];

export default function Expenses() {
  const qc = useQueryClient();
  const { can } = useApp();
  const [approvalSearch, setApprovalSearch] = useState("");
  const [approvalSourceFilter, setApprovalSourceFilter] = useState("all");
  const [expenseSearch, setExpenseSearch] = useState("");
  const [expenseCategoryFilter, setExpenseCategoryFilter] = useState("all");
  const [expenseStatusFilter, setExpenseStatusFilter] = useState("all");

  const { data: approvals, isLoading } = useQuery({ queryKey: ["fin-approvals"], queryFn: () => get("/finance/approvals") });
  const { data: expenses = [] } = useQuery({ queryKey: ["expenses"], queryFn: () => get("/finance/expenses") });
  const { data: ccs = [] } = useQuery({ queryKey: ["cost-centers"], queryFn: () => get("/finance/cost-centers") });

  const resolve = useMutation({
    mutationFn: ({ id }) => post(`/finance/approvals/${id}/resolve`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["fin-approvals"] }); toast.success("Resolved"); },
  });
  const logExpense = useMutation({
    mutationFn: (body) => post("/finance/expenses", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["expenses"] }); toast.success("Expense logged"); },
  });

  const approvalSources = useMemo(
    () => Array.from(new Set((approvals || []).map((a) => a.source).filter(Boolean))),
    [approvals],
  );
  const expenseStatuses = useMemo(
    () => Array.from(new Set(expenses.map((e) => e.status).filter(Boolean))),
    [expenses],
  );

  const filteredApprovals = useMemo(() => {
    return (approvals || []).filter((a) => {
      if (approvalSearch && !a.description?.toLowerCase().includes(approvalSearch.toLowerCase())) return false;
      if (approvalSourceFilter !== "all" && a.source !== approvalSourceFilter) return false;
      return true;
    });
  }, [approvals, approvalSearch, approvalSourceFilter]);

  const filteredExpenses = useMemo(() => {
    return expenses.filter((e) => {
      if (expenseSearch && !e.description?.toLowerCase().includes(expenseSearch.toLowerCase())) return false;
      if (expenseCategoryFilter !== "all" && e.category !== expenseCategoryFilter) return false;
      if (expenseStatusFilter !== "all" && e.status !== expenseStatusFilter) return false;
      return true;
    });
  }, [expenses, expenseSearch, expenseCategoryFilter, expenseStatusFilter]);

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <div>
      <PageHeader title="Expenses & Approvals" subtitle="Cross-module approval queue and expense tracking.">
        <FormDialog title="Log Expense" testid="log-expense-modal"
          trigger={<Button data-testid="log-expense-btn"><Plus className="mr-1.5 h-4 w-4" />Log Expense</Button>}
          fields={[
            { name: "description", label: "Description", required: true, full: true },
            { name: "category", label: "Category", type: "select", required: true, options: ["Infrastructure", "Software", "Events", "Travel", "Other"] },
            { name: "amount", label: "Amount", type: "number", required: true },
            { name: "cost_center", label: "Cost Center", type: "select", options: ccs.map((c) => ({ value: c.id, label: c.name })) },
          ]}
          onSubmit={(v) => logExpense.mutateAsync(v)} />
      </PageHeader>
      <Section title="Cross-Module Approval Queue" className="mb-6" testid="approval-queue">
        {approvals?.length > 0 && (
          <div className="flex items-center gap-3 border-b border-border/60 p-3">
            <SearchInput testid="approvals-search" placeholder="Search description" value={approvalSearch} onChange={setApprovalSearch} />
            <Select value={approvalSourceFilter} onValueChange={setApprovalSourceFilter}>
              <SelectTrigger className="w-40" data-testid="approvals-source-filter"><SelectValue placeholder="All sources" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                {approvalSources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="divide-y divide-border">
          {!approvals?.length ? <div className="px-4 py-6 text-center text-sm text-muted-foreground">No pending approvals</div> :
           !filteredApprovals.length ? <div className="px-4 py-6 text-center text-sm text-muted-foreground">No approvals match your search or filters</div> :
           filteredApprovals.map((a) => (
            <div key={a.id} data-testid={`approval-${a.id}`} className="flex items-center gap-3 px-4 py-3">
              <StatusBadge family="blue" label={a.source} />
              <span className="flex-1 text-sm">{a.description}</span>
              <span className="font-medium">{money(a.amount)}</span>
              {can("finance", "approve") && (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => resolve.mutate({ id: a.id })}>Reject</Button>
                  <Button size="sm" onClick={() => resolve.mutate({ id: a.id })}>Approve</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section title="Recent Expenses">
          {expenses.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 border-b border-border/60 p-3">
              <SearchInput testid="expenses-search" placeholder="Search description" value={expenseSearch} onChange={setExpenseSearch} className="w-48" />
              <Select value={expenseCategoryFilter} onValueChange={setExpenseCategoryFilter}>
                <SelectTrigger className="w-36" data-testid="expenses-category-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={expenseStatusFilter} onValueChange={setExpenseStatusFilter}>
                <SelectTrigger className="w-36" data-testid="expenses-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {expenseStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="divide-y divide-border">
            {!expenses.length ? <div className="px-4 py-6 text-center text-sm text-muted-foreground">No expenses logged</div> :
             !filteredExpenses.length ? <div className="px-4 py-6 text-center text-sm text-muted-foreground">No expenses match your search or filters</div> :
             filteredExpenses.map((e) => (
              <div key={e.id} className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1"><div className="text-sm font-medium">{e.description}</div><div className="text-xs text-muted-foreground">{e.category}</div></div>
                <span className="font-medium">{money(e.amount)}</span>
                <StatusBadge status={e.status} />
              </div>
            ))}
          </div>
        </Section>
        <Section title="Cost Center Budgets">
          <div className="divide-y divide-border">
            {ccs.map((c) => {
              const pct = c.variance;
              const fam = pct < 70 ? "bg-emerald-500" : pct < 90 ? "bg-amber-500" : "bg-red-500";
              return (
                <div key={c.id} className="px-4 py-3">
                  <div className="mb-1 flex justify-between text-sm"><span className="font-medium">{c.name}</span><span className="text-muted-foreground">{pct}%</span></div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-muted"><div className={cn("h-full", fam)} style={{ width: `${Math.min(pct, 100)}%` }} /></div>
                </div>
              );
            })}
          </div>
        </Section>
      </div>
    </div>
  );
}
